import { useLayoutEffect, useMemo } from "react";
import { App as AntdApp, ConfigProvider } from "antd";
import zhCN from "antd/locale/zh_CN";

import { adminModalConfig } from "./adminModalConfig";
import { AuthBootstrap } from "./auth/AuthBootstrap";
import { AdminRouterProvider, staticRoutes } from "./router";
import { buildMenuRoutes } from "./router/menuRoutes";
import { useUserStore } from "@/stores/react/authStore";
import { AdminTableEmpty } from "@/views/components/TableEmpty";
import { installRequestErrorMessageDeduper } from "@/utils/requestErrorMessage";
import { shouldSuppressSelectEmpty } from "@/utils/selectEmptyState";
import {
    cancelPendingSuccessFallback,
    captureSuccessNotices,
    consumeVisibleRequestSuccessFallback,
    hasNewSuccessNotice,
    markRequestSuccessFallbackVisible,
    REQUEST_SUCCESS_FALLBACK_KEY,
    scheduleSuccessFallback
} from "@/utils/globalSuccessNotice";

/** 在路由挂载前把服务端会话对应的菜单树写入 Zustand，供头部和侧栏共同消费。 */
function RouteMenuBootstrap() {
    const token = useUserStore(state => state.token);
    const permissions = useUserStore(state => state.userInfo.permissions || []);
    const menus = useMemo(() => (token ? buildMenuRoutes(staticRoutes, permissions) : []), [permissions, token]);

    useLayoutEffect(() => {
        useUserStore.getState().setMenuList(menus);
    }, [menus]);

    return null;
}

/** 订阅 API 层的全局错误事件，使用 AntdApp 上下文确保消息挂载到当前应用顶部。 */
function GlobalMessageBridge() {
    const { message: messageApi } = AntdApp.useApp();

    useLayoutEffect(() => {
        const handleGlobalMessage = (event: Event) => {
            const detail = (
                event as CustomEvent<{ message?: unknown; type?: string; fallback?: boolean; key?: string }>
            ).detail;
            if (detail?.message == null || detail.message === "") return;
            const type =
                detail.type === "success" || detail.type === "warning" || detail.type === "info"
                    ? detail.type
                    : "error";
            if (type === "success" && detail.fallback) {
                const previousNotices = captureSuccessNotices();
                // 页面在请求完成后的 await 回调中同步展示成功提示；让该提示先完成渲染，再决定是否兜底。
                scheduleSuccessFallback(() => {
                    if (!hasNewSuccessNotice(previousNotices)) {
                        markRequestSuccessFallbackVisible();
                        messageApi.open({
                            key: REQUEST_SUCCESS_FALLBACK_KEY,
                            type,
                            content: String(detail.message)
                        });
                    }
                });
                return;
            }
            if (type === "success") cancelPendingSuccessFallback();
            const fallbackKey = type === "success" ? consumeVisibleRequestSuccessFallback() : undefined;
            messageApi.open({
                type,
                content: String(detail.message),
                ...(detail.key || fallbackKey ? { key: detail.key || fallbackKey } : {})
            });
        };
        window.addEventListener("zhihao:global-message", handleGlobalMessage);
        return () => window.removeEventListener("zhihao:global-message", handleGlobalMessage);
    }, [messageApi]);

    return null;
}

/**
 * 管理后台根组件。
 *
 * 对齐旧版 App.vue 的全局 Element Plus 配置：按钮不自动插入中文字符间距，
 * 分页跳转文案保持中文；页面业务组件继续由 zhihao-ui-react 提供。
 */
export function App() {
    // 在任何页面请求发起前安装静态错误 Toast 去重器，覆盖尚未迁移到 showRequestError 的旧页面。
    installRequestErrorMessageDeduper();
    // 全局保留 Ant Form.Item 的必填标识，避免迁移页面因配置覆盖而丢失红色星号。
    return (
        <ConfigProvider
            locale={{ ...zhCN, Pagination: { ...zhCN.Pagination, jump_to: "前往" } }}
            renderEmpty={componentName => {
                // Ant Select 的默认空态依赖当前搜索输入值，空关键词时避免展示误导性的“无数据”。
                const activeInput =
                    typeof document !== "undefined" && document.activeElement instanceof HTMLInputElement
                        ? document.activeElement
                        : null;
                return shouldSuppressSelectEmpty(componentName, activeInput) ? null : <AdminTableEmpty />;
            }}
            button={{ autoInsertSpace: false }}
            form={{ requiredMark: true, colon: false }}
            // 多选筛选项沿用旧 Vue：展开时保持下拉箭头，不切换成搜索图标；需要搜索的选择器在组件上显式开启。
            select={{ showSearch: false }}
            // 普通 Select 下拉层按 option 内容宽度展开；显式数字宽度仍由业务组件覆盖。
            popupMatchSelectWidth={false}
            // 迁移页面统一继承旧版 Vue 确认框的居中位置，单个 Modal 显式配置仍可覆盖。
            modal={adminModalConfig}
        >
            <AntdApp>
                <GlobalMessageBridge />
                <AuthBootstrap>
                    <RouteMenuBootstrap />
                    <AdminRouterProvider />
                </AuthBootstrap>
            </AntdApp>
        </ConfigProvider>
    );
}

export default App;
